import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const isSupabaseConfigured =
  supabaseUrl &&
  supabaseAnonKey &&
  supabaseUrl.startsWith('https://') &&
  !supabaseUrl.includes('your-project-ref') &&
  !supabaseAnonKey.includes('your-anon-key');

// The Emergent platform script wraps window.fetch and consumes response bodies
// for analytics, which breaks Supabase JS with "body stream already read".
// Workaround: implement a fetch-compatible function on top of XMLHttpRequest
// (which is NOT wrapped by the platform script).
const xhrFetch = (input, init = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const url = typeof input === 'string' ? input : input.url;
      const method = (init.method || 'GET').toUpperCase();
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      // Use default responseType so platform scripts that read responseText
      // (e.g. emergent-main.js analytics wrapper) don't throw.

      // Apply headers
      const headers = init.headers || {};
      if (headers instanceof Headers) {
        headers.forEach((v, k) => xhr.setRequestHeader(k, v));
      } else if (Array.isArray(headers)) {
        headers.forEach(([k, v]) => xhr.setRequestHeader(k, v));
      } else {
        Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      }

      if (init.signal) {
        if (init.signal.aborted) {
          xhr.abort();
          reject(new DOMException('Aborted', 'AbortError'));
          return;
        }
        init.signal.addEventListener('abort', () => xhr.abort());
      }

      xhr.onload = () => {
        const raw = xhr.getAllResponseHeaders() || '';
        const responseHeaders = new Headers();
        raw.trim().split(/[\r\n]+/).forEach((line) => {
          const idx = line.indexOf(':');
          if (idx > 0) responseHeaders.append(line.slice(0, idx).trim(), line.slice(idx + 1).trim());
        });
        // Statuses 1xx/204/205/304 must NOT carry a body per Fetch spec.
        const nullBodyStatus = xhr.status === 204 || xhr.status === 205 || xhr.status === 304 || (xhr.status >= 100 && xhr.status < 200);
        const body = nullBodyStatus ? null : (xhr.responseText || null);
        resolve(new Response(body, {
          status: xhr.status || 200,
          statusText: xhr.statusText,
          headers: responseHeaders,
        }));
      };
      xhr.onerror = () => reject(new TypeError('Network request failed'));
      xhr.ontimeout = () => reject(new TypeError('Network request timed out'));
      xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));

      xhr.send(init.body || null);
    } catch (err) {
      reject(err);
    }
  });
};

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      global: { fetch: xhrFetch },
    })
  : createClient('https://placeholder.supabase.co', 'placeholder-key');
